import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Volume2, VolumeX } from "lucide-react";
import { userHasInteracted } from "@/lib/speech";
import { readMagicSettings } from "@/lib/magic-engine";

// Event name emitted by magic-engine whenever MagicSettings are persisted.
const MAGIC_SETTINGS_CHANGE_EVENT = "jingle:magic:settings-change";

function readGlobalSoundEnabled(): boolean {
  try {
    return readMagicSettings().soundEnabled;
  } catch {
    return true;
  }
}

// Synthesizes a faint, distant jingle-bell ambience via Web Audio API. We
// schedule random soft "chimes" composed of a few overtones, low-pass
// filtered, with reverb-ish stereo delay, to evoke sleigh bells far off in
// the distance. No external audio files needed.
export function AmbientBells({
  enabled = true,
  soundEnabled: soundEnabledProp,
  bottomClass = "bottom-3",
}: {
  enabled?: boolean;
  /**
   * When provided, overrides the global MagicSettings.soundEnabled.
   * When omitted (auth/public pages that don't have the settings hook),
   * AmbientBells reads MagicSettings from localStorage directly and
   * subscribes to `jingle:magic:settings-change` so the global sound
   * preference is honoured without callers needing to wire the prop.
   * The local mute toggle (`jingle.bells.muted`) still works as a
   * secondary granular override — both must be "on" for bells to play.
   */
  soundEnabled?: boolean;
  /**
   * Tailwind class for the mute button's bottom offset. Defaults to
   * `bottom-3` (12px) which is fine on auth/legal screens that have no
   * bottom navigation. Surfaces with a sticky bottom nav (AppLayout)
   * should pass a larger offset like `bottom-24` so the button clears
   * the nav and doesn't intercept taps on the rightmost nav tab.
   *
   * NOTE: Tailwind's JIT compiler reads class names as literal source
   * strings, so consumers must pass the FULL class literal (e.g.
   * `"bottom-24"`) — interpolated names like `\`bottom-${n}\`` won't
   * be picked up by the scanner.
   */
  bottomClass?: string;
}) {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterRef = useRef<GainNode | null>(null);
  const timerRef = useRef<number | null>(null);
  const [muted, setMuted] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("jingle.bells.muted") === "1";
  });
  const [ready, setReady] = useState(false);

  // Effective soundEnabled: use the explicit prop if provided (e.g. AppLayout
  // passes settings.soundEnabled and owns reactivity via React re-renders).
  // When the prop is omitted (auth/public pages), initialize from persisted
  // MagicSettings and keep in sync with settings-change events.
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    if (soundEnabledProp !== undefined) return soundEnabledProp;
    if (typeof window === "undefined") return true;
    return readGlobalSoundEnabled();
  });

  // Track explicit prop changes (AppLayout re-renders with updated settings).
  useEffect(() => {
    if (soundEnabledProp !== undefined) setSoundEnabled(soundEnabledProp);
  }, [soundEnabledProp]);

  // When no explicit prop, subscribe to settings-change events so turning
  // off sound in Magic Settings silences bells on auth/public pages too.
  useEffect(() => {
    if (soundEnabledProp !== undefined) return;
    const handler = () => setSoundEnabled(readGlobalSoundEnabled());
    window.addEventListener(MAGIC_SETTINGS_CHANGE_EVENT, handler);
    return () => window.removeEventListener(MAGIC_SETTINGS_CHANGE_EVENT, handler);
  }, [soundEnabledProp]);

  // Keep the local `muted` state in sync with the shared
  // `jingle.bells.muted` localStorage flag. The new header speaker
  // button (AppLayout) and Magic Settings can both flip this flag
  // out from under us; without this listener, the floating bells
  // button would show a stale icon and the master gain wouldn't
  // update until the next mount. Cross-tab edits arrive via
  // `storage`; same-tab edits arrive via the custom mute-change
  // event that every writer dispatches.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => {
      const next =
        window.localStorage.getItem("jingle.bells.muted") === "1";
      setMuted((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("jingle:ambient:mute-change", refresh);
    window.addEventListener(MAGIC_SETTINGS_CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("jingle:ambient:mute-change", refresh);
      window.removeEventListener(MAGIC_SETTINGS_CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    function ensureContext() {
      if (ctxRef.current) return ctxRef.current;
      const Ctor =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return null;
      const ctx: AudioContext = new Ctor();
      const master = ctx.createGain();
      // Respect both the global sound toggle and the local mute state at
      // context-creation time. The separate soundEnabled effect handles
      // subsequent changes, but if either is already off when the AudioContext
      // is first constructed (e.g. page reload with sound off), the gain must
      // start at 0 so no bells play before the effect runs.
      master.gain.value = muted || !soundEnabled ? 0 : 0.018;
      master.connect(ctx.destination);
      ctxRef.current = ctx;
      masterRef.current = master;
      return ctx;
    }

    function chime() {
      const ctx = ctxRef.current;
      const master = masterRef.current;
      if (!ctx || !master) return;
      const now = ctx.currentTime;
      // A bell is a fundamental + a few inharmonic overtones with quick
      // attack and long decay.
      const fundamentals = [880, 1175, 1568]; // pick one for variety
      const f0 = fundamentals[Math.floor(Math.random() * fundamentals.length)]!;
      const partials = [1, 2.0, 2.76, 4.07];
      const dur = 1.6 + Math.random() * 1.4;
      const bellGain = ctx.createGain();
      bellGain.gain.setValueAtTime(0.0001, now);
      bellGain.gain.exponentialRampToValueAtTime(0.35, now + 0.01);
      bellGain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      // Soft low-pass to feel "distant"
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 2400;
      lp.Q.value = 0.7;
      bellGain.connect(lp);
      // Stereo widener: split, slight delay on right
      const splitter = ctx.createChannelSplitter(2);
      const merger = ctx.createChannelMerger(2);
      const delayR = ctx.createDelay(0.1);
      delayR.delayTime.value = 0.018;
      lp.connect(splitter);
      splitter.connect(merger, 0, 0);
      splitter.connect(delayR);
      delayR.connect(merger, 0, 1);
      merger.connect(master);

      partials.forEach((p, i) => {
        const osc = ctx.createOscillator();
        osc.type = i === 0 ? "sine" : "triangle";
        osc.frequency.value = f0 * p;
        const partialGain = ctx.createGain();
        partialGain.gain.value = i === 0 ? 0.4 : 0.18 / (i + 1);
        osc.connect(partialGain).connect(bellGain);
        osc.start(now);
        osc.stop(now + dur + 0.05);
      });

      // Rare double-jingle, much less often than before.
      if (Math.random() < 0.12) {
        scheduleNext(0.22 + Math.random() * 0.3);
      }
    }

    function scheduleNext(after?: number) {
      if (cancelled) return;
      const delay = after ?? 16 + Math.random() * 18; // 16-34s between bells
      timerRef.current = window.setTimeout(() => {
        if (cancelled) return;
        try {
          chime();
        } catch {}
        scheduleNext();
      }, delay * 1000);
    }

    function start() {
      const ctx = ensureContext();
      if (!ctx) return;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      setReady(true);
      // Initial chime after a short, gentle delay.
      scheduleNext(8 + Math.random() * 6);
    }

    // Start once the user has interacted (browser autoplay policy).
    if (userHasInteracted()) {
      start();
    } else {
      const onInteract = () => {
        start();
        window.removeEventListener("pointerdown", onInteract);
        window.removeEventListener("keydown", onInteract);
      };
      window.addEventListener("pointerdown", onInteract);
      window.addEventListener("keydown", onInteract);
      return () => {
        cancelled = true;
        window.removeEventListener("pointerdown", onInteract);
        window.removeEventListener("keydown", onInteract);
        if (timerRef.current) window.clearTimeout(timerRef.current);
        teardownAudio();
      };
    }

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      teardownAudio();
    };

    // Close the AudioContext on unmount/disable. Browsers cap concurrent
    // contexts (Chrome ~6) and a leaked one keeps the audio worklet thread
    // alive — under React StrictMode dev double-invoke each remount would
    // otherwise leak one. Now that AppLayout mounts AmbientBells alongside
    // auth/legal pages that already mount it, the route-level remount on
    // sign-in is also a real teardown trigger in production. close() is
    // idempotent-safe-ish but throws on already-closed; swallow.
    function teardownAudio() {
      const ctx = ctxRef.current;
      if (ctx) {
        try {
          ctx.close();
        } catch {}
      }
      ctxRef.current = null;
      masterRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Apply mute state. Both the global `soundEnabled` prop and the local
  // `muted` toggle must be "on" for the bells to play. If either is off,
  // the master gain is forced to 0. Restore-on-unmute matches the initial
  // gain in ensureContext() (0.018 — "very faint background"). The
  // previous 0.06 value was 3.3× louder and caused a surprise loudness
  // bump on first unmute.
  useEffect(() => {
    if (!masterRef.current) return;
    masterRef.current.gain.value = muted || !soundEnabled ? 0 : 0.018;
  }, [muted, soundEnabled]);

  function toggle() {
    setMuted((m) => {
      const next = !m;
      try {
        window.localStorage.setItem("jingle.bells.muted", next ? "1" : "0");
      } catch {}
      // Notify same-tab listeners (the `storage` event only fires
      // cross-tab). The home-screen fireplace ambience subscribes to
      // this event so the mute toggle silences it instantly.
      try {
        window.dispatchEvent(new Event("jingle:ambient:mute-change"));
      } catch {}
      return next;
    });
  }

  if (!enabled) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={toggle}
      title={muted ? "Unmute bells" : "Mute bells"}
      aria-label={muted ? "Unmute bells" : "Mute bells"}
      className={`fixed ${bottomClass} right-3 z-[150] h-9 w-9 rounded-full border border-white/15 bg-black/30 text-white/70 backdrop-blur hover:bg-black/40 hover:text-white`}
      data-testid="button-bells-toggle"
    >
      {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      <span className="sr-only">{ready ? "" : "starts after first tap"}</span>
    </Button>
  );
}
